// Must equal IOT_DOWNLOAD_MAX_HOURS in evcell-v1.1 config.py - the backend falls back to it
// for an org without the key, and rejects anything longer.
export const DEFAULT_DEVICE_HEALTH_DOWNLOAD_MAX_HOURS = 1;

// The dialog picks one date plus a start and end time on it, so it cannot express more than a day.
// The end-time picker cannot select 12:00 AM, so the longest pickable range is 00:00-23:59.
const SINGLE_DAY_HOURS = 24;

// The pickers work in whole minutes; a smaller limit would reject every range. Must equal
// DEVICE_HEALTH_DOWNLOAD_MIN_HOURS in evcell-v1.1 app/services/iot_service.py.
export const MIN_DEVICE_HEALTH_DOWNLOAD_MAX_HOURS = 1 / 60;

/**
 * Longest time range the Device Health download dialog allows, from org config.
 *
 * Source is orgMeta.metaInfo.globalConfig.data.deviceHealthDownloadMaxHours - the same key the
 * backend enforces (evcell-v1.1 app/services/iot_service.py, getDeviceHealthDownloadMaxHours):
 *
 *   "globalConfig": {
 *     "data": { "hotDays": 30, "deviceHealthDownloadMaxHours": 6 }
 *   }
 *
 * The backend copies globalConfig onto every page's metaInfo, so the Digital Twin pageMeta
 * already carries it.
 *
 * @param {object} metaInfo - the `metaInfo` object from a pageMeta response
 * @returns {number} hours, between 1 minute and 24 hours
 */
export const getDeviceHealthDownloadMaxHours = (metaInfo) => {
    const configured = Number(metaInfo?.globalConfig?.data?.deviceHealthDownloadMaxHours);
    if (!Number.isFinite(configured) || configured < MIN_DEVICE_HEALTH_DOWNLOAD_MAX_HOURS) {
        return DEFAULT_DEVICE_HEALTH_DOWNLOAD_MAX_HOURS;
    }
    return Math.min(configured, SINGLE_DAY_HOURS);
};

/**
 * Message of a failed download. The request uses responseType "blob", so an error body
 * ({ responseStatus: { msg } }) arrives as a Blob and has to be read first.
 *
 * @param {object} err - the axios error
 * @returns {Promise<string|null>} the backend message, or null when there is none
 */
export const getDownloadErrorMessage = async (err) => {
    const data = err?.response?.data;
    try {
        const text = typeof data?.text === "function" ? await data.text() : data;
        const body = typeof text === "string" ? JSON.parse(text) : text;
        return body?.responseStatus?.msg || null;
    } catch {
        return null;
    }
};
